/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import reduxDialogThirdParty, { openDialog, closeDialog } from 'redux-dialog';
import { autobind } from 'core-decorators';

import { isDialogOpen } from 'dialogSelector';
import { clearReduxDataErrors } from 'actionCreators';

/**
 * This HOC is a bridge between the reduxStore and our "presentational" BaseModal component, the key option is reduxPath
 *
 * This HOC adds these properties to the wrapped component:
 * - onClose: PropTypes.func, will close the current dialog if invoked
 * - actions.openDialog: PropTypes.func, takes a dialog id as an argument allows opening other dialogs from this one
 * - actions.closeDialog: PropTypes.func, takes a dialog id as an argument closes that dialog
 * - actions.clearReduxDataErrors: PropTypes.func, clears the current reduxPath error.
 *
 */

const propTypes = {
  actions: PropTypes.objectOf(PropTypes.func),
  isOpen: PropTypes.bool,
};

const defaultProps = {
  isOpen: false,
  actions: {},
};

export const reduxDialog =
  ({
    name, // Name of the dialog that is used in redux for open/closed state
    reduxPath, // Looks into this location redux store to determine whether the data is in error or being fetched
    ...other
  }) =>
  WrappedDialog => {
    const mapStateToProps = state => ({
      isOpen: isDialogOpen(state, name),
    });

    const mapDispatchToProps = dispatch => ({
      actions: bindActionCreators({ openDialog, closeDialog, clearReduxDataErrors }, dispatch),
    });

    @connect(mapStateToProps, mapDispatchToProps)
    class ReduxDialog extends Component {
      WrappedDialogWithRedux = reduxDialogThirdParty({
        // Have to wrap every one with custom options to fix the rendering and accessibility
        className: 'noop',
        name,
        appElement: document.getElementById('mount'),
        ...other,
      })(WrappedDialog);

      @autobind
      handleClose() {
        const {
          actions: { closeDialog: closeDialogAction },
        } = this.props;
        closeDialogAction(name);
      }

      @autobind
      handleClearErrors() {
        const {
          actions: { clearReduxDataErrors: clearReduxDataErrorsAction },
        } = this.props;
        if (clearReduxDataErrorsAction) {
          clearReduxDataErrorsAction(reduxPath);
        }
      }

      render() {
        const { isOpen, ...other } = this.props; // eslint-disable-line @typescript-eslint/no-shadow

        return isOpen ? (
          <this.WrappedDialogWithRedux
            dialogId={name}
            onClose={this.handleClose}
            onClearDialogErrors={this.handleClearErrors}
            isOpen={isOpen}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
          />
        ) : null;
      }
    }

    ReduxDialog.propTypes = propTypes;
    ReduxDialog.defaultProps = defaultProps;

    return ReduxDialog;
  };
